import type { FlowPieceKind, RoutingVersion, VersionStep } from '@arcnet/contracts';
import { describeStep, lineText, type Names } from './describe.js';

/**
 * The version list (docs/41 §12, Fable's design): versions as rows, newest first. Classic saves by
 * one person on one page, each within 15 minutes of the next, share a row: twelve saves while
 * somebody tunes a ring time are one thing to read. Each is still a version of its own; restoring the
 * row restores its last save. Publishes, restores and named versions are never folded.
 */

export interface Row {
  /** The version the row stands for: its newest. */
  id: string;
  /** Newest first; more than one for a folded run of saves. */
  versions: RoutingVersion[];
  /** What the row changed, from before its oldest version to after its newest. */
  steps: VersionStep[];
}

const FOLD_MS = 15 * 60 * 1000;

export function rowsOf(versions: RoutingVersion[]): Row[] {
  const rows: Row[] = [];
  for (const v of versions) {
    const last = rows[rows.length - 1];
    const prev = last?.versions[last.versions.length - 1];
    const folds = !!prev && v.source === 'classic' && prev.source === 'classic' && !v.name && !prev.name
      && v.by === prev.by && v.page === prev.page
      && new Date(prev.at).getTime() - new Date(v.at).getTime() <= FOLD_MS;
    if (folds) last!.versions.push(v);
    else rows.push({ id: v.id, versions: [v], steps: [] });
  }
  for (const r of rows) r.steps = netSteps(r.versions);
  return rows;
}

/** A run's steps, net: each step from before the run's first save to after its last. */
function netSteps(vs: RoutingVersion[]): VersionStep[] {
  if (vs.length === 1) return vs[0]!.steps;
  const out = new Map<string, VersionStep>();
  for (const v of [...vs].reverse()) {
    for (const s of v.steps) {
      const k = `${s.kind}:${s.id}`;
      const was = out.get(k);
      out.set(k, was ? { ...s, before: was.before } : s);
    }
  }
  return [...out.values()].filter((s) => JSON.stringify(s.before) !== JSON.stringify(s.after));
}

export const PAGE_LABEL: Record<FlowPieceKind, string> = {
  inbound_route: 'Inbound routes page', ivr: 'IVR menus page', ring_group: 'Hunt lists page', queue: 'Queues page',
  message: 'Call steps page', hours: 'Call steps page',
};

const pad = (n: number) => String(n).padStart(2, '0');
/** "14:12" */
export const clock = (iso: string) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/** "3 Oct, 14:12" */
export const shortAt = (iso: string) => { const d = new Date(iso); return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${clock(iso)}`; };
/** "18 September" */
export const dayMonth = (iso: string) => { const d = new Date(iso); return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]}`; };

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
/** "Today · Wednesday 7 October", "Yesterday · …", "Friday 3 October" (the year when it is not this one). */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const base = `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS_LONG[d.getMonth()]}${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
  if (dayKey(d) === dayKey(now)) return `Today · ${base}`;
  if (dayKey(d) === dayKey(yesterday)) return `Yesterday · ${base}`;
  return base;
}
export const sameDay = (a: string, b: string) => dayKey(new Date(a)) === dayKey(new Date(b));

/**
 * Where a version came from, as the row says it. A step's own history also names the route a publish
 * or a route's restore was for (`withRoute`); a route's history is that route's already.
 */
export function sourceText(v: RoutingVersion, withRoute = false): string {
  const route = withRoute && v.route ? ` (${v.route})` : '';
  switch (v.source) {
    case 'start': return 'where history starts';
    case 'classic': return v.page ? PAGE_LABEL[v.page] : 'Classic';
    case 'flow': return v.restored_from
      ? `published from the flow${route} · restored from ${shortAt(v.restored_from.at)}` : `published from the flow${route}`;
    case 'restore': return v.page ? `restored on the ${PAGE_LABEL[v.page]}` : `restored${route}`;
  }
}

/** The row's second line: the setting itself when it is one, else how many and which. */
export function summaryOf(row: Row, n: Names): { text: string; was?: string | undefined } {
  const v = row.versions[0]!;
  if (v.source === 'start') return { text: 'The routing as it was when history began' };
  if (v.source === 'restore' && v.restored_from) {
    const to = `Back to ${shortAt(v.restored_from.at)}${v.restored_from.name ? ` · ${v.restored_from.name}` : ''}`;
    return { text: row.steps.length > 0 ? `${to} · ${row.steps.map((s) => s.name).join(', ')}` : to };
  }
  if (row.steps.length === 0) return { text: 'No change to this route' };
  if (row.steps.length === 1) {
    const s = row.steps[0]!;
    const lines = describeStep(s.kind, s.before, s.after, n);
    if (lines.length === 1) return { text: `${s.name} · ${lines[0]!.text}`, was: lines[0]!.was };
    return { text: `${s.name} · ${lines.length} settings` };
  }
  return { text: `${row.steps.length} steps · ${row.steps.map((s) => s.name).join(', ')}` };
}

/** A row in one string, for titles and labels. */
export const summaryText = (row: Row, n: Names) => lineText(summaryOf(row, n));
